/**
 * Normalize and validate goal deadline / target_date values for date inputs and APIs.
 * Prevents malformed timestamps (e.g. timezone-prefixed years) from reaching Postgres.
 */

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_DATE_PREFIX = /^(\d{4}-\d{2}-\d{2})[T\s]/;
const MIN_YEAR = 1970;
const MAX_YEAR = 2100;

/** Optional date string from forms or API payloads. */
export type OptionalDateInput = string | null | undefined;

/**
 * Is Real Calendar Date.
 */
function isRealCalendarDate(year: number, month: number, day: number): boolean {
  if (year < MIN_YEAR || year > MAX_YEAR) {
    return false;
  }
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return false;
  }
  const utc = new Date(Date.UTC(year, month - 1, day));
  return (
    utc.getUTCFullYear() === year && utc.getUTCMonth() === month - 1 && utc.getUTCDate() === day
  );
}

/**
 * Convert stored deadline / target_date into `YYYY-MM-DD` for `<input type="date">`.
 * Returns empty string when the value cannot be normalized safely.
 */
export function toDateInputValue(raw: OptionalDateInput): string {
  if (raw == null) {
    return "";
  }
  const trimmed = String(raw).trim();
  if (!trimmed) {
    return "";
  }

  const dateOnly = DATE_ONLY.exec(trimmed);
  if (dateOnly) {
    const year = Number(dateOnly[1]);
    const month = Number(dateOnly[2]);
    const day = Number(dateOnly[3]);
    return isRealCalendarDate(year, month, day) ? trimmed : "";
  }

  const isoPrefix = ISO_DATE_PREFIX.exec(trimmed);
  if (isoPrefix) {
    const [year, month, day] = isoPrefix[1].split("-").map(Number);
    return isRealCalendarDate(year, month, day) ? isoPrefix[1] : "";
  }

  // Reject timezone-displacement style values like "+012027-03-12T00:00:00.000Z"
  if (/^[+-]\d{2,}/.test(trimmed)) {
    return "";
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  const year = parsed.getUTCFullYear();
  const month = parsed.getUTCMonth() + 1;
  const day = parsed.getUTCDate();
  if (!isRealCalendarDate(year, month, day)) {
    return "";
  }

  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * True when value is a safe calendar date suitable for goal target_date / deadline.
 */
export function isValidGoalDeadline(raw: OptionalDateInput): boolean {
  if (raw == null) {
    return false;
  }
  const normalized = toDateInputValue(raw);
  return DATE_ONLY.test(normalized);
}

/**
 * Normalize for API payloads. Returns null for empty input; throws for invalid values.
 */
export function normalizeGoalDeadlineForApi(raw: OptionalDateInput): string | null {
  if (raw == null || String(raw).trim() === "") {
    return null;
  }
  const normalized = toDateInputValue(raw);
  if (!normalized) {
    throw new Error("Invalid goal deadline. Use a calendar date between 1970 and 2100.");
  }
  return normalized;
}
